import React, { useContext } from 'react';

import { HeadFC } from 'gatsby';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { StaticImage } from 'gatsby-plugin-image';

import ClaimsAccordionSection from '../../components/claimsAccordionSection/ClaimsAccordionSection';
import ClaimsMoreInfo from '../../components/claimsMoreInfo/ClaimsMoreInfo';
import Shape from '../../components/Shape';
import { PhoneNumbersContext } from '../../context/PhoneNumbersProvider';
import formatPhoneNumber from '../../helpers/formatPhoneNumber';
import HeaderImageBreakpoints from '../../types/breakpoints';
import {
  CatSvg,
  DesktopDogSvg,
  Divider,
  MobileCatImageContainer,
  MobileDogImageContainer,
  MoreInfoContainer,
  MoreInfoLinks,
  MoreInfoLinksContainer,
  MoreInfoParagraph,
  MoreInfoTitle,
  PhoneContainer,
  PhoneSvg,
  TabletCatImageContainer,
  TabletDogSvg,
} from './pet-more-info.style';

export type MoreInfoTypes = {
  title: React.ReactNode;
  paragraph: React.ReactNode;
  links?: React.ReactNode;
};

const getCatImage = ({ isMobile, isTablet }: HeaderImageBreakpoints) => {
  if (isMobile) {
    return (
      <MobileCatImageContainer>
        <StaticImage
          src="../../images/MobileCat.png"
          alt="Insurance for people like you"
          layout="fullWidth"
          loading="eager"
          placeholder="blurred"
        />
      </MobileCatImageContainer>
    );
  }
  if (isTablet) {
    return (
      <TabletCatImageContainer>
        <StaticImage
          src="../../images/TabletCat.png"
          alt="Insurance for people like you"
          layout="fullWidth"
          loading="eager"
          placeholder="blurred"
        />
      </TabletCatImageContainer>
    );
  }
};

const getDogImage = ({ isMobile }: HeaderImageBreakpoints) => {
  if (isMobile) {
    return (
      <MobileDogImageContainer>
        <StaticImage
          src="../../images/MobileDog.png"
          alt="Insurance for people like you"
          layout="fullWidth"
          loading="eager"
          placeholder="blurred"
        />
      </MobileDogImageContainer>
    );
  }
};

const PetMoreInfo = () => {
  const phoneNumbers = useContext(PhoneNumbersContext);
  const { isMobile, isTablet, isDesktop } = useBreakpoint();
  const catImage = getCatImage({ isMobile, isTablet });
  const dogImage = getDogImage({ isMobile, isTablet });

  const moreInfoData: MoreInfoTypes = {
    title: <MoreInfoTitle>We&apos;re here to help</MoreInfoTitle>,
    paragraph: (
      <MoreInfoParagraph $isTop>
        We know how troubling it can be when your beloved pet is poorly, and
        making a claim can be additional stress on top of an already worrying
        time. We always try to keep things simple, so we&apos;ve covered some of
        our most commonly asked questions below, so you know what to expect
        throughout your claims experience with us.
      </MoreInfoParagraph>
    ),
  };

  const moreInfoQuestionData: MoreInfoTypes = {
    title: <MoreInfoTitle>Have more questions?</MoreInfoTitle>,
    paragraph: (
      <MoreInfoParagraph>
        We&apos;ll continue to keep you updated as your claim progresses.{' '}
        {isTablet && !isMobile && <br />} If you have any questions,
        {isTablet && !isMobile && <br />} you can get in touch with our
        dedicated claims team below.
      </MoreInfoParagraph>
    ),
    links: (
      <MoreInfoLinksContainer>
        <MoreInfoLinks href="mailto:pet-claims@policyexpert.co.uk">
          pet-claims@policyexpert.co.uk
        </MoreInfoLinks>
        {!isMobile && <Divider />}
        <PhoneContainer>
          <PhoneSvg />
          <MoreInfoLinks
            href={`tel:${formatPhoneNumber(phoneNumbers.pet?.claims)}`}
          >
            {phoneNumbers.pet?.claims}
          </MoreInfoLinks>
        </PhoneContainer>
      </MoreInfoLinksContainer>
    ),
  };
  return (
    <MoreInfoContainer>
      <ClaimsMoreInfo data={moreInfoData} />
      <ClaimsAccordionSection />
      <ClaimsMoreInfo data={moreInfoQuestionData} />
      {isMobile ? dogImage : isTablet ? <TabletDogSvg /> : <DesktopDogSvg />}
      <CatSvg />
      {catImage}
      {isDesktop && <Shape position="top-left-pet-claims" />}
      {isDesktop && <Shape position="bottom-right-pet-claims" />}
    </MoreInfoContainer>
  );
};

export default PetMoreInfo;

export const Head: HeadFC = () => (
  <>
    <title>How to make a claim | Pet Information | Policy Expert</title>
    <meta
      name="description"
      content="Read more about how to make a pet insurance claim with Policy Expert - insurance for people like you."
    />
    <html lang="en" />
  </>
);
